/**
 * Height to tangent-space normal. Works on plain `{ width, height, data }`
 * RGBA byte buffers so it runs, and is tested, without a DOM: the same
 * function serves the client's atlas and the dice lab's preview.
 *
 * Height is read from the red channel. The result follows three.js's
 * tangent-space convention (OpenGL: +X right, +Y up, +Z out), so a flat map
 * is (128, 128, 255) and a slope rising toward +X tilts the normal toward -X.
 * Edges clamp rather than wrap; the atlas cells are cut with a margin, so a
 * one-pixel seam artefact never lands on a face.
 */
export function normalFromHeight({ width, height, data }, strength = 1) {
  const out = new Uint8ClampedArray(width * height * 4);
  const h = (x, y) => {
    const cx = x < 0 ? 0 : x >= width ? width - 1 : x;
    const cy = y < 0 ? 0 : y >= height ? height - 1 : y;
    return data[(cy * width + cx) * 4] / 255;
  };
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      // Sobel: the gradient across three rows and three columns.
      const dx =
        (h(x + 1, y - 1) - h(x - 1, y - 1)) +
        2 * (h(x + 1, y) - h(x - 1, y)) +
        (h(x + 1, y + 1) - h(x - 1, y + 1));
      const dy =
        (h(x - 1, y + 1) - h(x - 1, y - 1)) +
        2 * (h(x, y + 1) - h(x, y - 1)) +
        (h(x + 1, y + 1) - h(x + 1, y - 1));
      // Canvas y runs down; tangent-space +Y runs up.
      let nx = -dx * strength;
      let ny = dy * strength;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len;
      ny /= len;
      nz /= len;
      const i = (y * width + x) * 4;
      out[i] = (nx * 0.5 + 0.5) * 255;
      out[i + 1] = (ny * 0.5 + 0.5) * 255;
      out[i + 2] = (nz * 0.5 + 0.5) * 255;
      out[i + 3] = 255;
    }
  }
  return { width, height, data: out };
}
